import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FiUsers, FiCheck, FiX, FiClock, FiShield, FiMail, FiCalendar, FiRefreshCw } from 'react-icons/fi';
import { getAuthToken } from '../../utils/authUtils';
import { PRIMARY_API_URL } from '../../config/api';

const UserManagement = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [processingUsers, setProcessingUsers] = useState(new Set());

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const response = await fetch(`${PRIMARY_API_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      
      // Separate users by status
      const pending = data.filter(user => user.approval_status === 'pending');
      const approved = data.filter(user => user.approval_status === 'approved' || !user.approval_status);
      const rejected = data.filter(user => user.approval_status === 'rejected');
      
      setPendingUsers(pending);
      setApprovedUsers(approved);
      setRejectedUsers(rejected);
      
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = async (userId, email, action) => {
    try {
      setProcessingUsers(prev => new Set([...prev, userId]));
      
      const token = await getAuthToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const response = await fetch(`${PRIMARY_API_URL}/api/admin/users/${userId}/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} user`);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`User ${action === 'approve' ? 'approved' : 'rejected'} successfully!`);
        // Refresh the users list
        await fetchUsers();
      } else {
        throw new Error(result.message || `Failed to ${action} user`);
      }
      
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      toast.error(`Failed to ${action} user`);
    } finally {
      setProcessingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  const getUserStats = () => ({
    pending: pendingUsers.length,
    approved: approvedUsers.length,
    rejected: rejectedUsers.length,
    total: pendingUsers.length + approvedUsers.length + rejectedUsers.length
  });

  const stats = getUserStats();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-12 w-12 border-4 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">User Management</h1>
          <p className="text-gray-400 mt-1">Manage admin registration requests</p>
        </div>
        <button
          onClick={fetchUsers}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-white text-black rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          <FiRefreshCw className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[#0a0a0a] rounded-xl p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Pending Requests</p>
              <p className="text-2xl font-bold text-white">{stats.pending}</p>
            </div>
            <div className="bg-white/10 p-3 rounded-full">
              <FiClock className="text-white text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-[#0a0a0a] rounded-xl p-6 border border-green-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Approved Users</p>
              <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
            </div>
            <div className="bg-green-500/10 p-3 rounded-full">
              <FiCheck className="text-green-500 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-[#0a0a0a] rounded-xl p-6 border border-red-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Rejected Users</p>
              <p className="text-2xl font-bold text-red-400">{stats.rejected}</p>
            </div>
            <div className="bg-red-500/10 p-3 rounded-full">
              <FiX className="text-red-500 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-[#0a0a0a] rounded-xl p-6 border border-blue-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Users</p>
              <p className="text-2xl font-bold text-blue-400">{stats.total}</p>
            </div>
            <div className="bg-blue-500/10 p-3 rounded-full">
              <FiUsers className="text-blue-500 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-800">
        <nav className="flex space-x-8">
          {[
            { id: 'pending', label: 'Pending Requests', count: stats.pending, color: 'amber' },
            { id: 'approved', label: 'Approved Users', count: stats.approved, color: 'green' },
            { id: 'rejected', label: 'Rejected Users', count: stats.rejected, color: 'red' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? `border-${tab.color}-400 text-${tab.color}-400`
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                  activeTab === tab.id 
                    ? `bg-${tab.color}-400/20 text-${tab.color}-400`
                    : 'bg-gray-700 text-gray-300'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* User Lists */}
      <div className="bg-[#0a0a0a] rounded-xl border border-gray-800">
        {activeTab === 'pending' && (
          <div>
            {pendingUsers.length === 0 ? (
              <div className="p-8 text-center">
                <FiClock className="mx-auto text-4xl text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No Pending Requests</h3>
                <p className="text-gray-500">All registration requests have been processed.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {pendingUsers.map((user) => (
                  <div key={user.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="bg-white/10 p-3 rounded-full">
                          <FiShield className="text-white text-xl" />
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-white">{user.email}</h3>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <FiCalendar className="text-xs" />
                              Requested: {formatDate(user.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <FiClock className="text-xs" />
                              Pending approval
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleUserAction(user.id, user.email, 'reject')}
                          disabled={processingUsers.has(user.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                          {processingUsers.has(user.id) ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <FiX />
                          )}
                          Reject
                        </button>
                        <button
                          onClick={() => handleUserAction(user.id, user.email, 'approve')}
                          disabled={processingUsers.has(user.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                          {processingUsers.has(user.id) ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <FiCheck />
                          )}
                          Approve
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'approved' && (
          <div>
            {approvedUsers.length === 0 ? (
              <div className="p-8 text-center">
                <FiCheck className="mx-auto text-4xl text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No Approved Users</h3>
                <p className="text-gray-500">No users have been approved yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {approvedUsers.map((user) => (
                  <div key={user.id} className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="bg-green-500/10 p-3 rounded-full">
                        <FiCheck className="text-green-500 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-white">{user.email}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <FiCalendar className="text-xs" />
                            Joined: {formatDate(user.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <FiCheck className="text-xs text-green-500" />
                            Approved admin
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'rejected' && (
          <div>
            {rejectedUsers.length === 0 ? (
              <div className="p-8 text-center">
                <FiX className="mx-auto text-4xl text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No Rejected Users</h3>
                <p className="text-gray-500">No registration requests have been rejected.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {rejectedUsers.map((user) => (
                  <div key={user.id} className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="bg-red-500/10 p-3 rounded-full">
                        <FiX className="text-red-500 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-white">{user.email}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <FiCalendar className="text-xs" />
                            Requested: {formatDate(user.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <FiX className="text-xs text-red-500" />
                            Access denied
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement; 